<template>
    <div>
        <v-ons-alert-dialog title="Add address" v-bind:visible.sync="show" data-test-id="dlg-address-picker">
            <v-ons-list class="section-h">
                <v-ons-list-item v-show="show_fields == false">
                    <div class="center">
                        <v-ons-input placeholder="Start typing your address" float ref="address_input"
                                     v-model="address_search" data-test-id="inp-search"/>
                    </div>
                </v-ons-list-item>
                <v-ons-list-item v-show="show_fields == false">
                    <div class="center">
                        <span v-show="google_places_error" class="warning-text warning-message">An error occurred when fetching the address details - please enter the address manually.</span>
                        <span class="link-button" v-on:click="show_fields = true" data-test-id="btn-show-fields">Enter manually</span>
                    </div>
                </v-ons-list-item>
                <v-ons-list-item v-show="show_fields">
                    <div class="center">
                        <v-ons-input placeholder="Building number / name" float v-model="new_address.building"
                                     name="address-building" data-test-id="inp-number"/>
                        <span v-show="show_building_valid_warning" class="warning-text">Please enter the building number / name if required.</span>
                    </div>
                </v-ons-list-item>
                <v-ons-list-item v-show="show_fields">
                    <div class="center">
                        <v-ons-input placeholder="Address" float v-model="new_address.address" name="address" data-test-id="inp-address"/>
                    </div>
                </v-ons-list-item>
                <v-ons-list-item v-show="show_fields" class="list-item-twocolumn">
                    <div class="center">
                        <v-ons-input placeholder="City" float v-model="new_address.city" name="city" data-test-id="inp-city"/>
                    </div>
                    <div class="right">
                        <v-ons-input placeholder="Postcode" float v-model="new_address.postcode" name="postcode" data-test-id="inp-postcode"/>
                    </div>
                </v-ons-list-item>
                <v-ons-list-item v-show="show_postcode_valid_warning">
                    <div class="center">
                        <span class="warning-text">Please ensure the postcode contains all spaces.</span>
                    </div>
                </v-ons-list-item>
                <v-ons-list-item>
                    <div class="center" v-show="show_fields">
                        <v-ons-input placeholder="Notes for courier" float v-model="new_address.notes" name="address-notes"
                                     data-test-id="inp-notes"/>
                        <span v-show="allergy_warning" class="warning-text">Notes must not relate to allergies.</span>
                    </div>
                </v-ons-list-item>
                <v-ons-list-item v-show="show_fields">
                    <div class="center">
                        <span class="link-button" v-on:click="show_fields = false">Search for address</span>
                    </div>
                </v-ons-list-item>
            </v-ons-list>
            <section class="section-vh" v-show="show_fields">
                <gtc-button modifier="large" v-on:click="submit" v-bind:disabled="can_save_address == false"
                            class="primary btn-action branded" data-test-id="btn-save">
                    Save
                </gtc-button>
            </section>
            <section class="section-vh">
                <gtc-button modifier="large" v-on:click="() => this.$emit('show', false)" class="secondary">
                    Cancel
                </gtc-button>
            </section>
        </v-ons-alert-dialog>

        <validation v-bind:show="validation.show"
                    v-bind:errors="validation.errors"
                    v-on:show="(show) => validation.show = show"></validation>
    </div>
</template>

<script>
    import GoogleMapsLoader from 'google-maps'
    import Vue from 'vue'

    import { default as ValidationComponent } from '../fragments/validation'

    export default {

        props: ['show', 'allergy_warning', 'country'],

        components: {
            'validation': ValidationComponent,
        },

        data() {
            return {
                app: this.$root.$data.app,
                initialised: false,
                waiting: false,
                show_fields: false,
                address_search: '',
                new_address: {
                    building: '',
                    address: '',
                    city: '',
                    postcode: '',
                    notes: '',
                    place: {
                        address: '',
                        city: '',
                        postcode: '',
                        id: '',
                    }
                },
                google_place: null,
                validation: {
                    show: false,
                    errors: [],
                },
                google_places_error: false,
            }
        },

        mounted() {

        },

        methods: {

            initPlacesAutocomplete(){

                if (this.initialised == true)
                    return

                GoogleMapsLoader.KEY = window.env.google_maps_key
                GoogleMapsLoader.VERSION = env.google_maps_version
                GoogleMapsLoader.LIBRARIES = ['places']

                GoogleMapsLoader.load(google => {

                    let ac_config = {}
                    if (this.country)
                        ac_config.componentRestrictions = { country: this.country }

                    const address_input = this.$refs.address_input.$el.querySelector('input')
                    const autocomplete = new google.maps.places.Autocomplete(address_input, ac_config)

                    // Set fields to collect
                    autocomplete.setFields(['name', 'address_components', 'types'])              

                    // When the user selects an address from the dropdown, populate the address
                    // fields in the form.
                    google.maps.event.addListener(autocomplete, 'place_changed', () => {

                        const place = autocomplete.getPlace()
                        const place_address_components = place.address_components

                        // Address components will be undefined if no place was selected. Ignore in this case as there
                        // is no data to process.
                        if (place_address_components == null) {
                            return
                        }

                        try {
                            this.google_place = place

                            const address_components = Object.values(place_address_components)
                                .reduce((dict, part) => {
                                    dict[part.types[0]] = part.long_name
                                    return dict
                                }, {});

                            console.log(address_components, place, place.name, place.types)

                            const is_business = place.types.includes('establishment')

                            // Get name and street number, usually provided for residential addresses
                            const building = address_components['street_number']

                            const address_parts = []
                            if (address_components.hasOwnProperty('route') && address_components['route'].length > 0)
                                address_parts.push(address_components['route'])

                            if (address_parts.length == 0 && address_components.hasOwnProperty('neighborhood') && address_components['neighborhood'].length > 0)
                                address_parts.push(address_components['neighborhood'])

                            // If no address so far, user the name
                            if (address_parts.length == 0)
                                address_parts.push(place.name)
                            // If this is a business and the business name is not part of the address, prepend the name
                            else if (address_parts.join(' ').includes(place.name) == false && is_business)
                                address_parts.unshift(place.name)

                            const address = address_parts.join(', ')
                            const city = address_components['locality'] || 
                                address_components['postal_town'] || 
                                address_components['administrative_area_level_1']
                            const postcode = address_components['postal_code']

                            this.new_address.building = building
                            this.new_address.address = address
                            this.new_address.city = city
                            this.new_address.postcode = postcode
                            this.new_address.notes = ''
                            this.new_address.place = {
                                address,
                                city,
                                postcode,
                                id: place.place_id,
                                types: place.types,
                            }

                            this.show_fields = true

                            this.google_places_error = false
                        } catch (error) {
                            this.google_places_error = true
                            this.app.logException(new Error('Error on Google place change.'), { error, place })
                        }
                    });
                })

                setTimeout(() => {

                    var address_list = document.getElementsByClassName('pac-container')[0]

                    if (address_list == null) {
                        return
                    }

                    address_list.addEventListener('touchend', e => {
                        // Prevent touch screen devices from overriding the place_changed event
                        e.stopImmediatePropagation()
                    })
                }, 500)

                this.initialised = true
            },

            focusPlacesAutocomplete(){
                Vue.nextTick(() => this.$refs.address_input.$el.querySelector('input').focus())
            },

            submit() {
                this.waiting = true
                this.$emit('savedAddress', {
                    address: this.address_line_1,
                    city: this.new_address.city,
                    postcode: this.new_address.postcode,
                    notes: this.new_address.notes,
                    place: this.new_address.place,
                })
            }
        },

        computed: {

            can_save_address: function (){
                if (this.waiting)
                    return false

                return [
                    this.new_address.address,
                    this.new_address.city,
                    this.new_address.postcode
                ].filter(x => x && x.length > 0).length == 3
            },

            show_postcode_valid_warning: function(){
                return this.new_address.postcode != null &&
                    this.new_address.postcode.length > 4 &&
                    this.new_address.postcode.includes(' ') == false
            },

            show_building_valid_warning: function(){
                return this.new_address.building == null || this.new_address.building.length == 0
            },

            address_line_1: function(){
                return [
                        this.new_address.building,
                        this.new_address.address,
                    ]
                    .filter(x => x != null && x.length > 0)
                    .join(' ')
            }
        },

        watch: {
            show(new_value){
                // Switch to Google place entry on show
                this.show_fields = !new_value

                // If showing again, reset form
                if (new_value == true) {
                    this.waiting = false
                    this.address_search = ''
                    this.new_address.building = ''
                    this.new_address.address = ''
                    this.new_address.city = ''
                    this.new_address.postcode = ''
                    this.new_address.notes = ''
                    this.focusPlacesAutocomplete()
                    this.initPlacesAutocomplete()

                    this.google_places_error = false
                }
            },

            show_fields(new_value){
                if (new_value == false)
                    this.focusPlacesAutocomplete()
            }
        }
    }
</script>

<style>
    .pac-container {
        z-index: 20000 !important;
    }

    .warning-message {
        text-align: center;
        margin-bottom: 10px;
    }
</style>


// WEBPACK FOOTER //
// src/js/components/fragments/address_picker.vue